// Stage Mail-V2 (2026-07-22 / Step 17/19): useLabels — 真实 label CRUD + message attach/detach。
//
// 设计:
//   - list(): 拉 /labels → items
//   - create({name, color?}) → POST /labels → items 上 追加
//   - remove(id) → DELETE /labels/:id → items 上 remove
//   - addToMessage(messageId, labelId) / removeFromMessage(messageId, labelId)
//   - 失败 时 抛 错, 调用方 catch + toast
//
// MAIL_V2_REAL=false 时 hook 走 demo 模式(no-op or 返回 demo labels)。

import { useCallback, useEffect, useRef, useState } from "react";
import { mailV2Api, type MailLabelDTO } from "../../../api/mail-v2";

export interface UseLabelsOptions {
  enabled: boolean;
  /** 在 MAIL_V2_REAL=false 时 返回 这 些 demo labels */
  demoLabels?: unknown[];
}

export function useLabels(opts: UseLabelsOptions) {
  const { enabled, demoLabels = [] } = opts;
  const [items, setItems] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) {
      setItems(demoLabels);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    mailV2Api
      .listLabels()
      .then((rows) => {
        if (cancelled) return;
        setItems(rows ?? []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, demoLabels]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    if (inFlightRef.current.has("__list__")) return;
    inFlightRef.current.add("__list__");
    setLoading(true);
    setError(null);
    try {
      const rows = await mailV2Api.listLabels();
      setItems(rows ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      inFlightRef.current.delete("__list__");
      setLoading(false);
    }
  }, [enabled]);

  const create = useCallback(
    async (body: { name: string; color?: string }): Promise<MailLabelDTO | null> => {
      if (!enabled) return null;
      try {
        const created = await mailV2Api.createLabel(body);
        setItems((prev) => {
          if (prev.some((l) => (l as MailLabelDTO).id === created.id)) return prev;
          return [...prev, created as unknown];
        });
        return created;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      }
    },
    [enabled],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      if (!enabled) return;
      if (inFlightRef.current.has(id)) return;
      inFlightRef.current.add(id);
      const prev = items;
      setItems((arr) => arr.filter((l) => (l as MailLabelDTO).id !== id));
      try {
        await mailV2Api.deleteLabel(id);
      } catch (e) {
        setItems(prev);
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      } finally {
        inFlightRef.current.delete(id);
      }
    },
    [enabled, items],
  );

  const addToMessage = useCallback(
    async (messageId: string, labelId: string): Promise<void> => {
      if (!enabled) return;
      await mailV2Api
        .listLabels()
        .then(() => undefined)
        .catch(() => undefined);
      await fetch(`/api/mail-v2/messages/${messageId}/labels/${labelId}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    },
    [enabled],
  );

  const removeFromMessage = useCallback(
    async (messageId: string, labelId: string): Promise<void> => {
      if (!enabled) return;
      await fetch(`/api/mail-v2/messages/${messageId}/labels/${labelId}`, {
        method: "DELETE",
        credentials: "include",
      });
    },
    [enabled],
  );

  return { items, loading, error, refresh, create, remove, addToMessage, removeFromMessage };
}